import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import BasicInput from '$components/Inputs/BaseInput';
import { useSendReferralRequestMutation } from '$store/modules/candidate-referrals';
import classes from './SendReferenceRequestModal.module.css';
import { ReactComponent as CloseIcon } from '../../../../icons/CloseBlue.svg';

interface Props {
  candidateId: number;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormValues {
  emails: string[];
}

const SendReferenceRequestModal: FC<Props> = ({
  candidateId,
  isOpen,
  setIsOpen,
}) => {
  Modal.setAppElement(document.getElementById('root') as HTMLElement);
  const [t] = useTranslation('global');
  const [sendReferralRequest] = useSendReferralRequestMutation();
  const [fieldIds, setFieldIds] = useState<number[]>([0]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      emails: [''],
    },
  });

  const emailFields = watch('emails');

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleAddEmail = () => {
    const currentEmails = getValues('emails');
    if (currentEmails.length < 5) {
      const newId = fieldIds[fieldIds.length - 1] + 1;
      setFieldIds([...fieldIds, newId]);
      const newEmails = [...currentEmails, ''];
      setValue('emails', newEmails);
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedEmails = [...getValues('emails')];
    updatedEmails[index] = value;
    setValue('emails', updatedEmails);
  };

  const onSubmit = ({ emails }: FormValues) => {
    emails.forEach((email) => {
      if (email) {
        sendReferralRequest({
          candidateId,
          ReferralEmail: email,
        }).finally(() => {
          setIsOpen(false);
          reset();
        });
      }
    });
  };

  return (
    <Modal
      className={classes.modal}
      overlayClassName={classes.overlay}
      isOpen={isOpen}
      onRequestClose={handleClose}
      ariaHideApp={false}
    >
      <div className={classes.closeIcon} onClick={handleClose} aria-hidden>
        <CloseIcon />
      </div>
      <h3 className={classes.title}>{t('ReferenceRequestModal.Title')}</h3>
      <p className={classes.subText}>{t('ReferenceRequestModal.SubTitle')}</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ul className={classes.inputsContainer}>
          {emailFields.map((_, index) => {
            const { ref, onChange, onBlur, name } = register(
              `emails.${index}`,
              {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format',
                },
              }
            );

            return (
              <li key={`email-${fieldIds[index]}`}>
                <BasicInput
                  type="email"
                  placeholder="example@email.com"
                  name={name}
                  refProp={ref}
                  onChange={(e) => {
                    onChange(e);
                    handleEmailChange(index, e.target.value);
                  }}
                  onBlur={onBlur}
                  errorText={errors.emails?.[index]?.message}
                />
              </li>
            );
          })}
          <div className={classes.buttonsContainer}>
            <button
              type="button"
              onClick={handleAddEmail}
              disabled={emailFields.length >= 5}
              className={classes.addButton}
            >
              {t('ReferenceRequestModal.AddMore')}
            </button>
            <button
              type="submit"
              className={classes.sendButton}
              disabled={
                !isDirty ||
                (Array.isArray(errors.emails) && errors.emails.some((e) => e))
              }
            >
              {t('ReferenceRequestModal.Send')}
            </button>
          </div>
        </ul>
      </form>
    </Modal>
  );
};

export default SendReferenceRequestModal;
